import { Pie } from "@visx/shape";
import { motion } from "motion/react";
import { useState } from "react";

import type { Inventory } from "../api/queries";
import { Panel } from "../components/Panel";
import { CHART } from "../components/charts/chartTokens";
import { useChartTransition } from "../components/charts/useChartTransition";
import { quantity, share, shortDate, tonnes } from "../lib/format";
import styles from "./InventoryAnalytics.module.css";

interface Props {
  inventory: Inventory;
  onOpenScope: (scope: string) => void;
  onOpenSite: (id: string) => void;
  scopeConflict?: (scope: string) => string | undefined;
  siteConflict?: (id: string) => string | undefined;
  /** Whether the monthly chart above this section is on screen and reconciles. It decides one
   * sentence: monthly trends are said to be absent only where the chart cannot show them (the
   * series failed, is loading, is empty or did not add up), because "not shown" printed beside a
   * chart that is shown is a false sentence. Defaults to false, the original wording. */
  monthlyShown?: boolean;
}

const SCOPES = ["1", "2", "3"] as const;
const COLOURS = { "1": CHART.scope1, "2": CHART.scope2, "3": CHART.complete };
const SITE_LIMIT = 7;

export function InventoryAnalytics({ inventory, onOpenScope, onOpenSite, scopeConflict, siteConflict, monthlyShown = false }: Props) {
  const transition = useChartTransition();
  const [scopeTableOpen, setScopeTableOpen] = useState(false);
  const scopes = SCOPES.map((scope) => ({
    scope,
    total: inventory.totals_by_scope.find((total) => total.scope === scope),
  }));
  const assessed = scopes.flatMap(({ scope, total }) =>
    total?.assessed ? [{ scope, kgco2e: total.kgco2e }] : [],
  );
  // The server's headline (finding 8), not a sum of the cards made here.
  const total = inventory.total_kgco2e;
  const drawable = assessed.every((scope) => Number(scope.kgco2e) >= 0)
    && Number.isFinite(Number(total)) && Number(total) > 0;
  const basis = inventory.scope2_method === "market" ? "market-based" : "location-based";

  return (
    <section className={styles.analytics} aria-label="Inventory breakdowns" data-testid="inventory-analytics">
      <p className={styles.context}>
        Whole workspace · {shortDate(inventory.period_start)}–{shortDate(inventory.period_end)} · {basis} Scope 2.
        Register filters do not change these totals. Explore changes scope or site only; your
        other register filters stay in force.
      </p>
      <div className={styles.grid}>
        <Panel title="Share by scope" sub="Share of the recorded, assessed amounts">
          <div className={styles.scopeLayout}>
            {drawable ? (
              <svg viewBox="0 0 180 180" className={styles.donut} role="group" aria-label="Recorded emissions by scope">
                <g transform="translate(90,90)">
                  <Pie
                    data={assessed.filter((scope) => Number(scope.kgco2e) > 0)}
                    pieValue={(scope) => Number(scope.kgco2e)}
                    pieSort={null}
                    pieSortValues={null}
                    innerRadius={57}
                    outerRadius={79}
                    padAngle={0.025}
                  >
                    {({ arcs, path }) => arcs.map((arc) => (
                      <motion.path
                        key={arc.data.scope}
                        role="button"
                        tabIndex={scopeConflict?.(arc.data.scope) ? -1 : 0}
                        aria-disabled={scopeConflict?.(arc.data.scope) ? true : undefined}
                        aria-label={`Explore scope ${arc.data.scope}: ${tonnes(arc.data.kgco2e)} tCO₂e${scopeConflict?.(arc.data.scope) ? `; ${scopeConflict(arc.data.scope)}` : ""}`}
                        fill={COLOURS[arc.data.scope]}
                        className={styles.arc}
                        initial={false}
                        animate={{ d: path(arc) ?? "" }}
                        transition={transition}
                        onClick={() => { if (!scopeConflict?.(arc.data.scope)) onOpenScope(arc.data.scope); }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            if (!scopeConflict?.(arc.data.scope)) onOpenScope(arc.data.scope);
                          }
                        }}
                      />
                    ))}
                  </Pie>
                </g>
              </svg>
            ) : (
              <p className={styles.empty}>
                {assessed.length === 0
                  ? "No assessed scope totals were returned."
                  : Number(total) === 0
                    ? "The recorded total is zero; there is no share to draw."
                    : "Scope shares cannot be drawn for these amounts."}
              </p>
            )}
            <ul className={styles.legend}>
              {scopes.map(({ scope, total: scopeTotal }) => (
                <li key={scope}>
                  {scopeTotal?.assessed ? (
                    <button
                      type="button"
                      disabled={Boolean(scopeConflict?.(scope))}
                      aria-description={scopeConflict?.(scope)}
                      className={styles.scopeButton}
                      aria-label={`Explore scope ${scope}, ${tonnes(scopeTotal.kgco2e)} tCO₂e${drawable ? `, ${share(scopeTotal.kgco2e, total)} of recorded total` : ""}`}
                      onClick={() => onOpenScope(scope)}
                    >
                      <span className={styles.swatch} style={{ background: COLOURS[scope] }} aria-hidden="true" />
                      <span>Explore scope {scope}</span>
                      <strong>{tonnes(scopeTotal.kgco2e)} tCO₂e</strong>
                      {drawable ? <span className={styles.percentage}>{share(scopeTotal.kgco2e, total)}</span> : null}
                      {scopeConflict?.(scope) ? <span className={styles.conflict}>{scopeConflict(scope)}</span> : null}
                    </button>
                  ) : <span className={styles.unassessed}>Scope {scope} · Not assessed</span>}
                </li>
              ))}
            </ul>
          </div>
          {inventory.scope3 ? <p className={styles.note}>{inventory.scope3.sentence}</p> : (
            <p className={styles.note}>Scope 3 assessment coverage was not returned.</p>
          )}
          <details className={styles.fallback} onToggle={(event) => setScopeTableOpen(event.currentTarget.open)}>
            <summary>View scope totals as a table</summary>
            {scopeTableOpen ? <table>
              <caption>Whole-workspace recorded scope totals, {basis}</caption>
              <thead><tr><th scope="col">Scope</th><th scope="col">tCO₂e</th><th scope="col">Recorded share</th></tr></thead>
              <tbody>
                {scopes.map(({ scope, total: scopeTotal }) => (
                  <tr key={scope}>
                    <th scope="row">Scope {scope}</th>
                    <td>{scopeTotal?.assessed ? tonnes(scopeTotal.kgco2e) : "Not assessed"}</td>
                    <td>{scopeTotal?.assessed && drawable ? share(scopeTotal.kgco2e, total) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table> : null}
          </details>
        </Panel>
        <SitePanel
          inventory={inventory}
          onOpenSite={onOpenSite}
          siteConflict={siteConflict}
          limit={SITE_LIMIT}
        />
      </div>
      <p className={styles.note}>
        Explore opens the source-figure register. Receipts show whole source figures; adding them
        is not the calendar-day allocated total shown here.{" "}
        {monthlyShown
          ? "Monthly trends are in the chart above. Sparklines, prior-period changes and emissions by activity are not shown: this response supplies no allocated series for them."
          : "Monthly trends, sparklines, prior-period changes and emissions by activity are not shown: this response supplies no allocated series for them."}
      </p>
    </section>
  );
}

/** The ranked site bars and their table. One component for the Overview's first seven and the "By
 * site" tab's every site, so the two cannot say different things about the same site: the label a
 * keyboard user hears, the disabled-with-a-reason state and the table alternative are written once. */
export function SitePanel({
  inventory,
  onOpenSite,
  siteConflict,
  limit,
}: {
  inventory: Inventory;
  onOpenSite: (id: string) => void;
  siteConflict?: (id: string) => string | undefined;
  /** How many bars to draw. Omitted draws every site, largest first. */
  limit?: number;
}) {
  const transition = useChartTransition();
  const [siteTableOpen, setSiteTableOpen] = useState(false);
  const everySite = limit === undefined;
  // **Sorted only when every site is drawn.** The Overview keeps the order the server returned (its
  // first seven are what the browser gate compares with the response); a sort there would change
  // which seven. The sign stays on the value, as the note below says.
  const ordered = everySite
    ? [...inventory.by_facility].sort((a, b) => Math.abs(Number(b.kgco2e)) - Math.abs(Number(a.kgco2e)))
    : inventory.by_facility;
  const visibleSites = ordered.slice(0, limit);
  const largestSite = Math.max(0, ...visibleSites.map((site) => Math.abs(Number(site.kgco2e))));
  const basis = inventory.scope2_method === "market" ? "market-based" : "location-based";
  return (
    <Panel
      title="By site"
      sub={everySite ? `All ${quantity(String(inventory.by_facility.length))} site totals, largest first` : `First ${quantity(String(visibleSites.length))} of ${quantity(String(inventory.by_facility.length))} site totals`}
    >
    {visibleSites.length ? (
      <ol className={styles.sites}>
        {visibleSites.map((site) => (
          <li key={site.facility_id}>
            <button
              type="button"
              disabled={Boolean(siteConflict?.(site.facility_id))}
              aria-description={siteConflict?.(site.facility_id)}
              className={styles.siteButton}
              aria-label={`Explore site ${site.facility_name}: ${tonnes(site.kgco2e)} tCO₂e`}
              onClick={() => onOpenSite(site.facility_id)}
            >
              <span className={styles.siteName}>{site.facility_name}</span>
              <span className={styles.track} aria-hidden="true">
                <motion.span
                  className={styles.fill}
                  initial={false}
                  animate={{ scaleX: largestSite > 0 ? Math.abs(Number(site.kgco2e)) / largestSite : 0 }}
                  transition={transition}
                />
              </span>
              <strong className={styles.value}>{tonnes(site.kgco2e)}</strong>
              {siteConflict?.(site.facility_id) ? <span className={styles.conflict}>{siteConflict(site.facility_id)}</span> : null}
            </button>
          </li>
        ))}
      </ol>
    ) : <p className={styles.empty}>No site totals were returned for this window.</p>}
    <p className={styles.note}>
      tCO₂e · All recorded scopes at each site, not a claim that Scope 3 assessment is complete.
      Bar lengths show magnitude; the values retain their sign.
    </p>
    <details className={styles.fallback} onToggle={(event) => setSiteTableOpen(event.currentTarget.open)}>
      <summary>View all site totals as a table</summary>
      {siteTableOpen ? <table>
        <caption>Whole-workspace site totals, {basis}</caption>
        <thead><tr><th scope="col">Site</th><th scope="col">tCO₂e</th></tr></thead>
        <tbody>
          {inventory.by_facility.map((site) => (
            <tr key={site.facility_id}>
              <th scope="row"><button type="button" disabled={Boolean(siteConflict?.(site.facility_id))} aria-description={siteConflict?.(site.facility_id)} onClick={() => { if (!siteConflict?.(site.facility_id)) onOpenSite(site.facility_id); }}>Explore {site.facility_name}</button>
                {siteConflict?.(site.facility_id) ? <span className={styles.conflict}>{siteConflict(site.facility_id)}</span> : null}
              </th>
              <td>{tonnes(site.kgco2e)}</td>
            </tr>
          ))}
        </tbody>
      </table> : null}
    </details>
  </Panel>
  );
}
